// Browser-side cache of CoPilot conversations (IndexedDB), so a page reload
// restores the chat in progress. Scoped per user — every key and payload
// carries the user id — and wiped on logout, so the next user of a shared
// panel never sees the previous user's conversation.

const IDB_NAME = "copilot_db";
const IDB_STORE = "sessions";
const CURRENT_MAX_AGE_MS = 12 * 60 * 60 * 1000;
const SESSION_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

function openIDB() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(IDB_NAME, 1);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(IDB_STORE)) {
        db.createObjectStore(IDB_STORE);
      }
    };
    req.onsuccess = () => {
      const db = req.result;
      // Yield to clearAll(): an open connection would block deleteDatabase.
      db.onversionchange = () => db.close();
      resolve(db);
    };
    req.onerror = () => reject(req.error);
  });
}

const currentKey = (userId) => `current:${userId}`;

/**
 * Save the conversation in progress for one user.
 * @param {number} userId
 * @param {string} sessionId
 * @param {Object[]} messages
 */
export async function saveSession(userId, sessionId, messages) {
  if (userId == null) return;
  try {
    const db = await openIDB();
    const tx = db.transaction(IDB_STORE, "readwrite");
    const store = tx.objectStore(IDB_STORE);
    const payload = { userId, sessionId, messages, savedAt: Date.now() };
    store.put(payload, currentKey(userId));
    store.put(payload, `${userId}:${sessionId}`);
    await new Promise((res, rej) => {
      tx.oncomplete = res;
      tx.onerror = () => rej(tx.error);
    });
    db.close();
  } catch (e) {
    console.warn("[CoPilot] IDB save failed:", e?.message);
  }
}

/**
 * The user's conversation in progress, if saved within the last 12 h.
 * @param {number} userId
 * @returns {Promise<?{userId: number, sessionId: string, messages: Object[], savedAt: number}>}
 */
export async function loadCurrentSession(userId) {
  if (userId == null) return null;
  try {
    const db = await openIDB();
    const tx = db.transaction(IDB_STORE, "readonly");
    const req = tx.objectStore(IDB_STORE).get(currentKey(userId));
    const data = await new Promise((res, rej) => {
      req.onsuccess = () => res(req.result);
      req.onerror = () => rej(req.error);
    });
    db.close();
    if (!data || data.userId !== userId) return null;
    if (Date.now() - (data.savedAt || 0) > CURRENT_MAX_AGE_MS) return null;
    return data;
  } catch (_) {
    return null;
  }
}

/**
 * Drop entries older than 7 days, and entries without an owner (written
 * before the cache was per user).
 */
export async function cleanOldSessions() {
  try {
    const db = await openIDB();
    const tx = db.transaction(IDB_STORE, "readwrite");
    tx.oncomplete = () => db.close();
    const store = tx.objectStore(IDB_STORE);
    const allKeys = await new Promise((res, rej) => {
      const r = store.getAllKeys();
      r.onsuccess = () => res(r.result);
      r.onerror = () => rej(r.error);
    });
    const cutoff = Date.now() - SESSION_MAX_AGE_MS;
    for (const key of allKeys) {
      const req = store.get(key);
      const val = await new Promise((r) => { req.onsuccess = () => r(req.result); req.onerror = () => r(null); });
      if (!val || val.userId == null || (val.savedAt && val.savedAt < cutoff)) {
        store.delete(key);
      }
    }
  } catch (_) {}
}

/** Delete the whole cache (logout). */
export function clearAll() {
  try { indexedDB.deleteDatabase(IDB_NAME); } catch (_) { /* storage unavailable */ }
}

/**
 * One cached conversation of this user (history view).
 * @param {number} userId
 * @param {string} sessionId
 * @returns {Promise<?Object>}
 */
export async function loadSession(userId, sessionId) {
  if (userId == null) return null;
  try {
    const db = await openIDB();
    const tx = db.transaction(IDB_STORE, "readonly");
    const req = tx.objectStore(IDB_STORE).get(`${userId}:${sessionId}`);
    const data = await new Promise((res, rej) => {
      req.onsuccess = () => res(req.result);
      req.onerror = () => rej(req.error);
    });
    db.close();
    return data && data.userId === userId ? data : null;
  } catch (_) {
    return null;
  }
}
